import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { UTILITY_USER_ROLE_ADMIN, hasRoleAdmin } from 'common/utility/userRoles';

import { SCREEN_ID_UTILITY_PROFIT_ADMIN_PARTNER_TOOLS } from 'client/constants/screenIds';

import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrButton from 'client/components/SrButton/SrButton';
import SrText from 'client/components/SrText';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

import UPPartnerSearchForm from 'client/components/UPPartnerSearchForm/UPPartnerSearchForm';
import PartnerStatusUpdateCard from 'client/components/SrUtilityProfit/PartnerStatusUpdateCard';
import TenantLiabilityBannerCard from 'client/components/SrUtilityProfit/TenantLiabilityBannerCard';

@branch(({ partnerSlug }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', partnerSlug],
  utilityUser: ['utilityUser'],
}))
class PartnerToolsContent extends Component {
  static propTypes = {
    utilityPartner: PropTypes.object,
    utilityUser: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    onClearPartner: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
    };
    this.savedTimeoutId = null;
  }

  componentWillUnmount() {
    if (this.savedTimeoutId) {
      clearTimeout(this.savedTimeoutId);
    }
  }

  _handleSuccess = () => {
    this.setState({ shouldShowSavedModal: true });

    if (this.savedTimeoutId) {
      clearTimeout(this.savedTimeoutId);
    }

    this.savedTimeoutId = setTimeout(() => {
      this.setState({ shouldShowSavedModal: false });
    }, 1500);
  };

  render() {
    const { utilityPartner, utilityUser, dispatch, onClearPartner } = this.props;
    const { shouldShowSavedModal } = this.state;

    if (!utilityPartner) {
      return null;
    }

    const isAdmin = hasRoleAdmin(utilityUser?.roles || []);

    return (
      <SrBox marginBottom="100px">
        <SrBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom="24px"
        >
          <SrText font="greycliff" size="20" weight="700">
            {utilityPartner?.name}
          </SrText>
          <SrButton type="button" onClick={onClearPartner} width="fit-content" padding="8px 20px">
            Change Partner
          </SrButton>
        </SrBox>
        <SrBox marginBottom="24px">
          <PartnerStatusUpdateCard
            utilityPartner={utilityPartner}
            isAdmin={isAdmin}
            dispatch={dispatch}
            onSuccess={this._handleSuccess}
          />
        </SrBox>
        {isAdmin && (
          <TenantLiabilityBannerCard
            utilityPartner={utilityPartner}
            dispatch={dispatch}
            onSuccess={this._handleSuccess}
          />
        )}
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrBox>
    );
  }
}

PartnerToolsContent.displayName = 'PartnerToolsContent';

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN_PARTNER_TOOLS)
@withScrollToTop
@allowedUtilityRoles({ roles: [UTILITY_USER_ROLE_ADMIN] })
@branch({})
class UtilityProfitAdminPartnerToolsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Tools',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedPartnerSlug: null,
      showSearchModal: false,
    };
  }

  _handleOpenSearch = () => {
    this.setState({ showSearchModal: true });
  };

  _handleCloseSearch = () => {
    this.setState({ showSearchModal: false });
  };

  _handlePartnerSelected = (partner) => {
    const { dispatch } = this.props;
    dispatch(UtilityPartnerActions.getBySlug, { slug: partner.slug });
    this.setState({ selectedPartnerSlug: partner.slug, showSearchModal: false });
  };

  _handleClearPartner = () => {
    this.setState({ selectedPartnerSlug: null });
  };

  render() {
    const { dispatch } = this.props;
    const { selectedPartnerSlug, showSearchModal } = this.state;

    return (
      <SrPartnerPageV2
        backUrl="/utility-profit/admin"
        navbarTitle="Partner Tools"
        navbarImage="/images/utility-profit-logo.svg"
        redirectToLastLocation={false}
      >
        {showSearchModal && (
          <UPPartnerSearchForm
            subtitle="Select a partner to manage their settings."
            onClose={this._handleCloseSearch}
            onPartnerSelected={this._handlePartnerSelected}
          />
        )}
        {!selectedPartnerSlug ? (
          <SrBox padding="30px 0">
            <SrButton type="button" onClick={this._handleOpenSearch}>
              Search Partner
            </SrButton>
          </SrBox>
        ) : (
          <PartnerToolsContent
            partnerSlug={selectedPartnerSlug}
            dispatch={dispatch}
            onClearPartner={this._handleClearPartner}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityProfitAdminPartnerToolsPage.displayName = 'UtilityProfitAdminPartnerToolsPage';

export default UtilityProfitAdminPartnerToolsPage;
